import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { CheckCircle2, AlertTriangle, XCircle, Activity, Database, HardDrive, Zap } from 'lucide-react';

const STATUS_CONFIG = {
  ok: { color: 'bg-green-50 text-green-800 border-green-200', dot: 'bg-green-500', icon: CheckCircle2, label: 'Operational' },
  degraded: { color: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500', icon: AlertTriangle, label: 'Degraded' },
  down: { color: 'bg-red-50 text-red-800 border-red-200', dot: 'bg-red-500', icon: XCircle, label: 'Down' },
  healthy: { color: 'bg-green-50 text-green-800 border-green-200', dot: 'bg-green-500', icon: CheckCircle2, label: 'Operational' },
};

const COMPONENTS = [
  { key: 'api', name: 'API Gateway', icon: Zap, description: 'Public API endpoints' },
  { key: 'database', name: 'Database', icon: Database, description: 'MongoDB · lead/user/transaction data' },
  { key: 'storage', name: 'Object Storage', icon: HardDrive, description: 'Lead source proof files' },
  { key: 'auth', name: 'Authentication', icon: Activity, description: 'JWT sessions and Google OAuth' },
];

export default function StatusPage() {
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/health');
        setHealth(res.data);
        setError(false);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
        setLastChecked(new Date());
      }
    };
    fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, []);

  const overall = error ? 'down' : (health?.status === 'ok' ? 'ok' : 'degraded');
  const overallCfg = STATUS_CONFIG[overall];
  const OverallIcon = overallCfg.icon;

  const componentStatus = (key) => {
    if (error) return 'down';
    if (!health) return 'ok';
    if (key === 'api') return 'ok';
    if (key === 'auth') return 'ok';
    return health.checks?.[key] === 'healthy' ? 'ok' : 'degraded';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="border-b border-gray-200 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono mb-2">
            Live · Auto-refreshes every 30 seconds
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Marketplace Status</h1>
          <p className="mt-2 text-sm text-gray-600 max-w-2xl">
            Real-time health of LeadXchange systems. Every component is checked from the frontend against the public /api/health endpoint.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8">
        {/* Overall Status */}
        <div className={`rounded-lg border p-6 flex items-center justify-between mb-6 ${overallCfg.color}`} data-testid="overall-status">
          <div className="flex items-center gap-4">
            <div className="relative">
              <OverallIcon className="w-8 h-8" strokeWidth={2} />
              <span className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full ${overallCfg.dot} animate-pulse`} />
            </div>
            <div>
              <div className="font-heading text-2xl font-semibold">
                {loading ? 'Checking…' :
                 overall === 'ok' ? 'All Systems Operational' :
                 overall === 'degraded' ? 'Some Systems Degraded' :
                 'Service Disruption'}
              </div>
              <div className="text-sm mt-1">
                {lastChecked ? `Last checked ${lastChecked.toLocaleTimeString()}` : 'Fetching...'}
              </div>
            </div>
          </div>
        </div>

        {/* Components */}
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
          {COMPONENTS.map(c => {
            const status = componentStatus(c.key);
            const cfg = STATUS_CONFIG[status];
            const Icon = c.icon;
            return (
              <div key={c.key} className="flex items-center justify-between p-5 hover:bg-gray-50" data-testid={`component-${c.key}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                    <Icon className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <div className="font-heading text-base font-semibold text-gray-900">{c.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{c.description}</div>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-2 text-sm font-medium px-3 py-1 border rounded ${cfg.color}`}>
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                  {cfg.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Meta */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <MetaBox label="Service" value={health?.service || 'leadxchange-api'} />
          <MetaBox label="Version" value={health?.version || '1.0.0'} />
          <MetaBox label="Endpoint" value="/api/health" mono />
        </div>

        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs text-gray-600">
          <strong className="text-gray-900">Incident reporting:</strong> If you notice degraded performance not shown here, email <span className="font-mono">status@leadxchange.com.au</span>. All incidents are logged in the audit system with resolution timestamps.
        </div>
      </div>
    </div>
  );
}

const MetaBox = ({ label, value, mono }) => (
  <div className="bg-white border border-gray-200 rounded p-3">
    <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
    <div className={`text-sm font-medium mt-1 ${mono ? 'font-mono' : ''}`}>{value}</div>
  </div>
);
